import { __decorate } from "tslib";
import { html, nothing } from 'lit';
import { CipublicElement } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/mixins/base.js';
import { property, state, query } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { FormRegistrationMixin } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/form/form-registration.js';
import { ValidityMixin } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/form/validity.js';
import { InternalI18n } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/i18n/internal-i18n-controller.js';
import { emit } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/event.js';
import { sanitize } from '@cigna/cigna_csg_gsg_cipublic-shared/lib/utils/sanitize.js';
import '@cigna/cigna_csg_gsg_cipublic-icon/cipublic-icon.js';
import styles from './textarea.scss.js';
let counter = 0;
/**
 * @param Base The base class.
 * @returns Textarea component.
 *
 * @fires textarea.blur
 * @fires textarea.input
 *
 * @event submit - Fires when the form is submitted.
 * @event formdata - Add form data to the submit process.
 */
export class Textarea extends FormRegistrationMixin(ValidityMixin(CipublicElement)) {
    constructor() {
        super(...arguments);
        this.i18n = new InternalI18n(this);
        /**
         * Sets the element to be focused automatically on page load.
         */
        this.autofocus = false;
        /**
         * Indicate if the element is disabled.
         *
         * **Note:** Will remove the element from `formdata` participation.
         */
        this.disabled = false;
        /**
         * Toggle element visibility.
         *
         * **Note:** Will remove the element from `formdata` participation.
         */
        this.hidden = false;
        /**
         * ID to associate the label with the element.
         */
        this._id = ++counter;
        /**
         * Constructor name.
         */
        this._name = 'textarea';
        /**
         * Optional Text.
         */
        this.optional = this.i18n.t('optional');
        /**
         * Indicate if the element is required.
         */
        this.required = false;
        /**
         * The internal value.
         */
        this._value = '';
        /**
         * Custom width for the element.
         */
        this.width = 'width-full';
        /**
         * Indicates how the control wraps text. Possible values are:
         *
         * `hard`: The browser automatically inserts line breaks (CR+LF) so that each line has no more
         * than the width of the control; the cols attribute must also be specified for this to take effect.
         *
         * `soft`: The browser ensures that all line breaks in the value consist of a CR+LF pair,
         * but does not insert any additional line breaks.
         *
         * If this attribute is not specified, `soft` is its default value.
         */
        this.wrap = 'soft';
    }
    static get styles() {
        return [styles];
    }
    /**
     * Excludes the element from `formdata` participation if `true`.
     *
     * **Note:** This property is enabled by default for `disabled` / `hidden` elements.
     */
    get deregistered() {
        return this.disabled || this.hidden ? true : false;
    }
    /**
     * Value of the text entered.
     */
    get value() {
        const { _textarea } = this;
        /*
          Once we have the textarea we can directly query for the value
          Otherwise, default to the internal `_value` prop.
        */
        return _textarea ? _textarea.value : this._value;
    }
    set value(value) {
        const { _textarea } = this;
        const oldValue = this._value;
        this._value = value;
        this.requestUpdate('value', oldValue);
        /*
          If we programtically modify the value through assignment,
          (example: sanitizing the value), update the textarea's value
        */
        if (_textarea) {
            _textarea.value = value;
        }
    }
    getFirstErroredInnerElement() {
        var _a;
        return (_a = this._getFirstErroredElement()) === null || _a === void 0 ? void 0 : _a.querySelector('textarea');
    }
    _handleFormSubmit(event) {
        var _a;
        const { _textarea } = this;
        this.value = sanitize(this.value);
        const error = this._showError(_textarea);
        this._showSuccess(_textarea);
        if (error) {
            // halt form submission behavior
            event.preventDefault();
            (_a = this.getFirstErroredInnerElement()) === null || _a === void 0 ? void 0 : _a.focus();
        }
        else {
            // dispatch 'FormData' event
            super._handleFormSubmit(event);
        }
    }
    updated(changedProperties) {
        super.updated(changedProperties);
        if (changedProperties.has('disabled')) {
            if (this.disabled) {
                this.value = '';
            }
        }
        if (changedProperties.has('errorCustomMessage')) {
            if (this.errorCustomMessage) {
                this._showError(this._textarea);
                this._showSuccess(this._textarea);
            }
        }
        if (changedProperties.has('required')) {
            if (!this.required) {
                this._showSuccess(this._textarea);
            }
        }
    }
    render() {
        var _a;
        const { autocomplete, autofocus, disabled, hint, label, maxlength, name, optional, placeholder, required, rows, spellcheckTextarea, _value, width, wrap } = this;
        const idName = `${this._name}-${this._id}`;
        return html `<div class="element-wrapper"><label for="${idName}">${label} ${!required ? optional : nothing}</label><div class="element-parent"><textarea aria-describedby="${idName}-hint ${idName}-count ${idName}-error" aria-invalid="${false}" autocomplete="${ifDefined(autocomplete)}" ?autofocus="${autofocus}" ?disabled="${((_a = this.closest('cipublic-fieldset')) === null || _a === void 0 ? void 0 : _a.disabled) ? true : disabled}" id="${idName}" maxlength="${ifDefined(maxlength)}" name="${ifDefined(name)}" placeholder="${ifDefined(placeholder)}" ?required="${disabled ? false : required}" rows="${ifDefined(rows)}" spellcheck="${spellcheckTextarea}" .value="${disabled ? '' : _value}" width="${width}" wrap="${wrap}" @blur="${(event) => {
            const status = this._testValidity(event.target);
            if (status !== this.errorCustomMessage) {
                this._showError(event.target);
            }
            this._showSuccess(event.target);
            emit(this, `${this._name}.blur`);
        }}" @input="${() => emit(this, `${this._name}.input`)}" @keyup="${async (event) => {
            if (maxlength) {
                this._count.innerText = `${this.value.length} / ${this.maxlength}`;
            }
            emit(this, `${this._name}.keyup`, { detail: { event } });
        }}"></textarea> <span id="after" hidden></span></div><div class="bottom-level"><div>${hint ? html `<span class="hint" id="${idName}-hint">${hint}</span>` : html `${nothing}`} <span aria-live="polite" class="error-container" id="${idName}-error" role="alert"><cipublic-icon set="system" name="exclamation-circle" class="error-icon"></cipublic-icon><p></p></span></div>${maxlength ? html `<span class="count" id="${idName}-count">${maxlength ? `0 / ${maxlength}` : ''}</span>` : html `${nothing}`}</div></div>`;
    }
}
__decorate([
    property({ reflect: true })
], Textarea.prototype, "autocomplete", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], Textarea.prototype, "autofocus", void 0);
__decorate([
    query('.count')
], Textarea.prototype, "_count", void 0);
__decorate([
    state()
], Textarea.prototype, "deregistered", null);
__decorate([
    property({ reflect: true, type: Boolean })
], Textarea.prototype, "disabled", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], Textarea.prototype, "hidden", void 0);
__decorate([
    property({ reflect: true })
], Textarea.prototype, "hint", void 0);
__decorate([
    property({ reflect: true })
], Textarea.prototype, "label", void 0);
__decorate([
    property({ reflect: true, type: Number })
], Textarea.prototype, "maxlength", void 0);
__decorate([
    property({ reflect: true })
], Textarea.prototype, "name", void 0);
__decorate([
    property({ reflect: true })
], Textarea.prototype, "optional", void 0);
__decorate([
    property({ reflect: true })
], Textarea.prototype, "placeholder", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], Textarea.prototype, "required", void 0);
__decorate([
    property({ reflect: true, type: Number })
], Textarea.prototype, "rows", void 0);
__decorate([
    property({ reflect: true, attribute: 'spellcheck' })
], Textarea.prototype, "spellcheckTextarea", void 0);
__decorate([
    query('textarea')
], Textarea.prototype, "_textarea", void 0);
__decorate([
    state()
], Textarea.prototype, "_value", void 0);
__decorate([
    property({ reflect: true })
], Textarea.prototype, "value", null);
__decorate([
    property({ reflect: true })
], Textarea.prototype, "width", void 0);
__decorate([
    property({ reflect: true })
], Textarea.prototype, "wrap", void 0);
//# sourceMappingURL=textarea.js.map